'use client'

import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import {
    MdAdd,
    MdBlock,
    MdCheckCircle,
    MdContentCopy,
    MdLink,
} from 'react-icons/md'
import ConfirmModal from '@/components/confirm-modal'
import {
    CreateRegistrationLinkData,
    REGISTRATION_LINK_STATUS_LABELS,
    RegistrationLink,
    RegistrationLinkStatus,
} from '@/lib/api/plans/plan-api'
import { ApiResponse } from '@/lib/api/request'
import { copyToClipboard } from '@/lib/utils/clipboard'

interface RegistrationLinksPanelProps {
    /**
     * Período grátis sugerido pelo plano, em dias. Só preenche o formulário: o
     * valor que vale é o digitado aqui, link a link.
     */
    defaultTrialDays?: number | null
    /** Carrega os links do plano/sub-plano. */
    onList: () => Promise<ApiResponse<RegistrationLink[]>>
    /** Cria mais um link — não invalida os já distribuídos. */
    onCreate: (
        data: CreateRegistrationLinkData,
    ) => Promise<ApiResponse<RegistrationLink>>
    /** Invalida um link específico. */
    onRevoke: (linkId: number) => Promise<ApiResponse<RegistrationLink>>
}

const STATUS_BADGE: Record<RegistrationLinkStatus, string> = {
    ACTIVE: 'badge-success',
    USED: 'badge-neutral',
    REVOKED: 'badge-error',
    EXPIRED: 'badge-warning',
}

/**
 * Links de cadastro de um plano. Criar um link novo NÃO invalida os que já estão
 * com os clientes, então dá para negociar com várias contas ao mesmo tempo.
 *
 * Cada link nasce de uso único (some ao ser usado), mas pode ser criado como
 * ILIMITADO para campanha — aí vale até expirar ou ser invalidado à mão.
 */
export default function RegistrationLinksPanel({
    defaultTrialDays,
    onList,
    onCreate,
    onRevoke,
}: RegistrationLinksPanelProps) {
    const [links, setLinks] = useState<RegistrationLink[]>([])
    const [loading, setLoading] = useState(true)
    const [creating, setCreating] = useState(false)
    const [showForm, setShowForm] = useState(false)
    const [label, setLabel] = useState('')
    const [expiresAt, setExpiresAt] = useState('')
    const [unlimited, setUnlimited] = useState(false)
    // Texto, e não número: o campo precisa aceitar vazio (= sem período grátis)
    // sem virar NaN enquanto o operador digita.
    const [trialDays, setTrialDays] = useState('')
    const [loadError, setLoadError] = useState<string | null>(null)
    const [linkToRevoke, setLinkToRevoke] = useState<RegistrationLink | null>(
        null,
    )
    const [revoking, setRevoking] = useState(false)

    useEffect(() => {
        loadLinks()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    /**
     * Falha ao carregar NÃO pode virar lista vazia: "Nenhum link criado ainda" é
     * indistinguível de "o servidor caiu", e o operador criaria um segundo link
     * achando que o primeiro não existe.
     */
    const loadLinks = async () => {
        setLoading(true)
        setLoadError(null)
        try {
            const response = await onList()
            if (response.success && response.result) {
                setLinks(response.result)
            } else {
                setLoadError(response.error || 'Erro ao carregar os links')
            }
        } catch {
            setLoadError('Erro ao carregar os links de cadastro')
        } finally {
            setLoading(false)
        }
    }

    const handleCreate = async () => {
        setCreating(true)
        try {
            const data: CreateRegistrationLinkData = {}
            if (label.trim()) data.label = label.trim()
            // O input date entrega só o dia; o link vale até o fim dele.
            if (expiresAt) {
                data.expiresAt = new Date(`${expiresAt}T23:59:59`).toISOString()
            }
            if (unlimited) data.unlimited = true
            // Sempre enviado (inclusive 0) para que o padrão do plano não
            // reapareça quando o operador apagar o campo de propósito.
            const parsedTrialDays = Number.parseInt(trialDays, 10)
            data.trialDays = Number.isFinite(parsedTrialDays)
                ? Math.max(parsedTrialDays, 0)
                : 0

            const response = await onCreate(data)
            if (response.success && response.result) {
                setLinks((current) => [response.result!, ...current])
                setLabel('')
                setExpiresAt('')
                setUnlimited(false)
                setTrialDays('')
                setShowForm(false)
                toast.success('Link criado. Os links anteriores continuam válidos.')
            } else {
                toast.error(response.error || 'Erro ao criar link')
            }
        } catch {
            toast.error('Erro ao criar link')
        } finally {
            setCreating(false)
        }
    }

    const handleRevoke = async () => {
        if (!linkToRevoke) return
        setRevoking(true)
        try {
            const response = await onRevoke(linkToRevoke.id)
            if (response.success && response.result) {
                setLinks((current) =>
                    current.map((item) =>
                        item.id === linkToRevoke.id ? response.result! : item,
                    ),
                )
                toast.success('Link invalidado')
                setLinkToRevoke(null)
            } else {
                toast.error(response.error || 'Erro ao invalidar link')
            }
        } catch {
            toast.error('Erro ao invalidar link')
        } finally {
            setRevoking(false)
        }
    }

    const activeCount = links.filter((link) => link.status === 'ACTIVE').length

    return (
        <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-base-content flex items-center gap-2">
                    <MdLink className="text-secondary" />
                    Links de Cadastro
                    {activeCount > 0 && (
                        <span className="badge badge-sm badge-success">
                            {activeCount} ativo{activeCount > 1 ? 's' : ''}
                        </span>
                    )}
                </h3>
                <button
                    type="button"
                    onClick={() => {
                        setShowForm((current) => {
                            // Ao abrir, o campo já vem com o padrão do plano —
                            // que o operador pode zerar para este cliente.
                            if (!current) {
                                setTrialDays(
                                    defaultTrialDays
                                        ? String(defaultTrialDays)
                                        : '',
                                )
                            }
                            return !current
                        })
                    }}
                    className="btn btn-sm btn-primary gap-1"
                >
                    <MdAdd className="text-base" />
                    Novo link
                </button>
            </div>

            <div className="alert alert-info mb-4 py-2">
                <span className="text-xs">
                    Criar um link novo <strong>não invalida</strong> os que já foram
                    enviados. Um link de <strong>uso único</strong> é invalidado
                    sozinho ao concluir um cadastro; um link{' '}
                    <strong>ilimitado</strong> aceita quantos cadastros vierem, até
                    a validade ou até você invalidá-lo.
                </span>
            </div>

            {showForm && (
                <div className="p-4 bg-base-200/40 rounded-xl mb-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="form-control w-full">
                            <label className="label pb-1" htmlFor="link-label">
                                <span className="label-text font-medium">
                                    Identificação
                                </span>
                            </label>
                            <input
                                id="link-label"
                                type="text"
                                value={label}
                                onChange={(event) => setLabel(event.target.value)}
                                placeholder="Ex.: Clínica São Lucas"
                                maxLength={120}
                                className="input input-bordered w-full"
                            />
                            <label className="label pt-1">
                                <span className="label-text-alt text-base-content/60">
                                    Só para você saber a quem enviou o link.
                                </span>
                            </label>
                        </div>

                        <div className="form-control w-full">
                            <label className="label pb-1" htmlFor="link-expires">
                                <span className="label-text font-medium">
                                    Validade
                                </span>
                            </label>
                            <input
                                id="link-expires"
                                type="date"
                                value={expiresAt}
                                onChange={(event) =>
                                    setExpiresAt(event.target.value)
                                }
                                className="input input-bordered w-full"
                            />
                            <label className="label pt-1">
                                <span className="label-text-alt text-base-content/60">
                                    Opcional. Em branco, vale até ser usado.
                                </span>
                            </label>
                        </div>

                        {/* O período grátis é deste link, não do plano: dá para
                            negociar 60 dias com um cliente e nenhum com outro
                            vendendo exatamente o mesmo plano. */}
                        <div className="form-control w-full">
                            <label className="label pb-1" htmlFor="link-trial">
                                <span className="label-text font-medium">
                                    Período grátis
                                </span>
                            </label>
                            <label className="input input-bordered w-full flex items-center gap-2">
                                <input
                                    id="link-trial"
                                    type="number"
                                    min={0}
                                    max={365}
                                    value={trialDays}
                                    onChange={(event) =>
                                        setTrialDays(event.target.value)
                                    }
                                    placeholder="0"
                                    className="grow"
                                />
                                <span className="text-base-content/50 text-sm">
                                    dias
                                </span>
                            </label>
                            <label className="label pt-1">
                                <span className="label-text-alt text-base-content/60">
                                    Vale só para quem se cadastrar por este link.
                                    Em branco ou 0, sem período grátis.
                                </span>
                            </label>
                        </div>
                    </div>

                    {/* Quantos cadastros o link aceita. Uso único é o padrão; o
                        ilimitado serve a campanha/divulgação aberta. */}
                    <fieldset className="form-control w-full mt-2">
                        <legend className="label pb-1">
                            <span className="label-text font-medium">
                                Quantidade de usos
                            </span>
                        </legend>
                        <div className="flex flex-col gap-2 sm:flex-row sm:gap-6">
                            <label className="label cursor-pointer justify-start gap-2 py-1">
                                <input
                                    type="radio"
                                    name="link-uses"
                                    className="radio radio-sm radio-primary"
                                    checked={!unlimited}
                                    onChange={() => setUnlimited(false)}
                                />
                                <span className="label-text">
                                    Uso único
                                    <span className="text-base-content/60">
                                        {' '}
                                        — vale por um cadastro
                                    </span>
                                </span>
                            </label>
                            <label className="label cursor-pointer justify-start gap-2 py-1">
                                <input
                                    type="radio"
                                    name="link-uses"
                                    className="radio radio-sm radio-primary"
                                    checked={unlimited}
                                    onChange={() => setUnlimited(true)}
                                />
                                <span className="label-text">
                                    Ilimitado
                                    <span className="text-base-content/60">
                                        {' '}
                                        — aceita quantos cadastros vierem
                                    </span>
                                </span>
                            </label>
                        </div>
                    </fieldset>

                    <div className="flex justify-end gap-2 mt-2">
                        <button
                            type="button"
                            onClick={() => {
                                setShowForm(false)
                                setLabel('')
                                setExpiresAt('')
                                setUnlimited(false)
                                setTrialDays('')
                            }}
                            className="btn btn-sm btn-ghost"
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            onClick={handleCreate}
                            disabled={creating}
                            className="btn btn-sm btn-primary gap-1"
                        >
                            {creating && (
                                <span className="loading loading-spinner loading-xs"></span>
                            )}
                            Criar link
                        </button>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="text-sm text-base-content/50">
                    Carregando links...
                </div>
            ) : loadError ? (
                <div className="alert alert-error py-2">
                    <span className="text-xs">
                        {loadError}. Recarregue a página para tentar de novo — os
                        links existentes não foram perdidos.
                    </span>
                </div>
            ) : links.length === 0 ? (
                <div className="text-sm text-base-content/50 p-4 bg-base-200/30 rounded-xl">
                    Nenhum link criado ainda. Crie um link para cada cliente que for
                    se cadastrar neste plano.
                </div>
            ) : (
                <div className="space-y-3">
                    {links.map((link) => (
                        <RegistrationLinkCard
                            key={link.id}
                            link={link}
                            onRevoke={() => setLinkToRevoke(link)}
                        />
                    ))}
                </div>
            )}

            <ConfirmModal
                isOpen={!!linkToRevoke}
                title="Invalidar link de cadastro"
                message={
                    linkToRevoke
                        ? `O link "${
                              linkToRevoke.label || `#${linkToRevoke.id}`
                          }" deixará de funcionar imediatamente. Os demais links deste plano não são afetados.`
                        : ''
                }
                confirmButtonText="Invalidar"
                cancelButtonText="Cancelar"
                loading={revoking}
                onConfirm={handleRevoke}
                onClose={() => setLinkToRevoke(null)}
            />
        </div>
    )
}

function RegistrationLinkCard({
    link,
    onRevoke,
}: {
    link: RegistrationLink
    onRevoke: () => void
}) {
    const isActive = link.status === 'ACTIVE'
    const isUnlimited = link.maxUses == null
    const companies = link.usedByCompanies || []

    return (
        <div className="p-4 bg-base-200/30 rounded-xl">
            <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                <div className="min-w-0">
                    <p className="text-sm font-semibold text-base-content truncate flex items-center gap-2">
                        {link.label || `Link #${link.id}`}
                        <span
                            className={`badge badge-xs ${
                                isUnlimited ? 'badge-info' : 'badge-ghost'
                            }`}
                        >
                            {isUnlimited ? 'Ilimitado' : 'Uso único'}
                        </span>
                        {!!link.trialDays && (
                            <span className="badge badge-xs badge-success">
                                {link.trialDays} dias grátis
                            </span>
                        )}
                    </p>
                    <p className="text-xs text-base-content/60 mt-0.5">
                        Criado em{' '}
                        {format(new Date(link.createdAt), "dd/MM/yyyy 'às' HH:mm", {
                            locale: ptBR,
                        })}
                        {link.expiresAt && (
                            <>
                                {' · '}
                                Válido até{' '}
                                {format(new Date(link.expiresAt), 'dd/MM/yyyy', {
                                    locale: ptBR,
                                })}
                            </>
                        )}
                    </p>
                    {link.usedCount > 0 && (
                        <p className="text-xs text-base-content/60 mt-0.5">
                            {link.usedCount === 1
                                ? '1 cadastro'
                                : `${link.usedCount} cadastros`}
                            {link.lastUsedAt && (
                                <>
                                    {' · '}
                                    último em{' '}
                                    {format(
                                        new Date(link.lastUsedAt),
                                        'dd/MM/yyyy',
                                        { locale: ptBR },
                                    )}
                                </>
                            )}
                            {companies.length > 0 && (
                                <>
                                    {': '}
                                    {companies
                                        .map((company) => company.name || `#${company.id}`)
                                        .join(', ')}
                                    {/* A lista vem limitada às 20 mais recentes;
                                        o total verdadeiro é `usedCount`. */}
                                    {link.usedCount > companies.length && (
                                        <>
                                            {' e mais '}
                                            {link.usedCount - companies.length}
                                        </>
                                    )}
                                </>
                            )}
                        </p>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <span className={`badge ${STATUS_BADGE[link.status]} badge-sm`}>
                        {REGISTRATION_LINK_STATUS_LABELS[link.status]}
                    </span>
                    {isActive && (
                        <button
                            type="button"
                            onClick={onRevoke}
                            className="btn btn-xs btn-ghost text-error gap-1"
                        >
                            <MdBlock className="text-sm" />
                            Invalidar
                        </button>
                    )}
                </div>
            </div>

            {isActive ? (
                <div className="space-y-2">
                    <CopyLinkRow
                        label="Pessoa Jurídica (PJ)"
                        type="pj"
                        token={link.token}
                    />
                    <CopyLinkRow
                        label="Pessoa Física (PF)"
                        type="pf"
                        token={link.token}
                    />
                </div>
            ) : (
                <p className="text-xs text-base-content/50">
                    Este link não aceita mais cadastros.
                </p>
            )}
        </div>
    )
}

/**
 * Uma linha copiável. PF e PJ são o MESMO link (muda só o tipo na URL) — quem
 * usar um consome o cadastro do outro.
 */
function CopyLinkRow({
    label,
    type,
    token,
}: {
    label: string
    type: 'pf' | 'pj'
    token: string
}) {
    const [copied, setCopied] = useState(false)

    const registerLink = `${window.location.origin}/register?type=${type}&token=${token}`

    const handleCopy = async () => {
        if (await copyToClipboard(registerLink)) {
            setCopied(true)
            toast.success('Link copiado!')
            setTimeout(() => setCopied(false), 2000)
        } else {
            toast.error('Erro ao copiar link')
        }
    }

    return (
        <div className="flex items-center gap-2">
            <span className="text-xs text-base-content/60 w-40 flex-shrink-0">
                {label}
            </span>
            <code className="flex-1 min-w-0 text-xs font-mono bg-base-100 px-2 py-1.5 rounded truncate">
                {registerLink}
            </code>
            <button
                type="button"
                onClick={handleCopy}
                className="btn btn-xs btn-ghost gap-1 flex-shrink-0"
            >
                {copied ? (
                    <MdCheckCircle className="text-success text-sm" />
                ) : (
                    <MdContentCopy className="text-sm" />
                )}
                Copiar
            </button>
        </div>
    )
}
